import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ButtonModule } from '@syncfusion/ej2-angular-buttons';
import { TextBoxModule } from '@syncfusion/ej2-angular-inputs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { environment } from '../environments/environment';
import { LoginService } from '../service/login.service';
import { UserService } from '../service/user.service';
import { AuthBrandPanelComponent } from '../shared/auth-brand-panel/auth-brand-panel.component';
import { MigrationNoticeComponent } from '../shared/migration-notice/migration-notice.component';

/**
 * Prefix of the `window.name` value that marks a pending one-time email-verification reload.
 * Namespaced and versioned so it cannot be mistaken for any other use of `window.name`.
 * The marker carries only an arming timestamp and the tab's previous `window.name`; no session data.
 */
export const EMAIL_VERIFICATION_RELOAD_MARKER_PREFIX = 'regtrack:emailVerificationReload:v1:';

/**
 * How long a reload marker stays valid. A reload completes in well under this; an older marker is
 * stale (left by an interrupted reload) and must not suppress a later, legitimate reload.
 */
export const EMAIL_VERIFICATION_RELOAD_MARKER_TTL_MS = 60000;

/** Tolerated clock skew for a marker stamped slightly in the future. */
const EMAIL_VERIFICATION_RELOAD_MARKER_SKEW_MS = 5000;

/** A parsed reload marker. */
interface ReloadMarker {
  /** Epoch milliseconds at which the marker was armed. */
  armedAt: number;
  /** The tab's `window.name` before the marker was armed (restored when the marker is consumed). */
  originalName: string;
}

/**
 * Parses a `window.name` value that may carry a reload marker.
 * @param name - The current `window.name`.
 * @returns The parsed marker, or `null` when the name carries no well-formed marker.
 */
export function parseReloadMarker(name: string): ReloadMarker | null {
  if (typeof name !== 'string' || !name.startsWith(EMAIL_VERIFICATION_RELOAD_MARKER_PREFIX)) {
    return null;
  }
  const rest = name.slice(EMAIL_VERIFICATION_RELOAD_MARKER_PREFIX.length);
  const sep = rest.indexOf(':');
  if (sep < 1) {
    return null;
  }
  const armedAt = Number(rest.slice(0, sep));
  if (!Number.isFinite(armedAt)) {
    return null;
  }
  let originalName = '';
  try {
    originalName = decodeURIComponent(rest.slice(sep + 1));
  } catch {
    originalName = '';
  }
  return { armedAt, originalName };
}

/**
 * Arms the one-time reload marker in `window.name`, remembering the tab's previous name so it can be
 * restored. Re-arming never nests markers: an existing (even stale or malformed) marker is replaced.
 * @param win - The window whose `name` carries the marker.
 * @param now - Current time in epoch milliseconds (injectable for tests).
 * @returns void. Side effect: sets `win.name`.
 */
export function armReloadMarker(win: Pick<Window, 'name'>, now: number = Date.now()): void {
  const existing = parseReloadMarker(win.name);
  const original = existing
    ? existing.originalName
    : (typeof win.name === 'string' && win.name.startsWith(EMAIL_VERIFICATION_RELOAD_MARKER_PREFIX) ? '' : (win.name ?? ''));
  win.name = `${EMAIL_VERIFICATION_RELOAD_MARKER_PREFIX}${now}:${encodeURIComponent(original)}`;
}

/**
 * Consumes the reload marker: restores the tab's previous `window.name` (so no marker is ever left
 * behind) and reports whether a FRESH marker was present, i.e. whether the one-time reload already happened.
 * A stale or malformed marker is removed but reported as absent, so it cannot suppress a legitimate reload.
 * @param win - The window whose `name` carries the marker.
 * @param now - Current time in epoch milliseconds (injectable for tests).
 * @returns `true` when a marker armed within {@link EMAIL_VERIFICATION_RELOAD_MARKER_TTL_MS} was present; otherwise `false`.
 * Side effect: removes any marker from `win.name`.
 */
export function consumeReloadMarker(win: Pick<Window, 'name'>, now: number = Date.now()): boolean {
  if (typeof win.name !== 'string' || !win.name.startsWith(EMAIL_VERIFICATION_RELOAD_MARKER_PREFIX)) {
    return false;
  }
  const marker = parseReloadMarker(win.name);
  win.name = marker ? marker.originalName : '';
  if (!marker) {
    return false;
  }
  const age = now - marker.armedAt;
  return age >= -EMAIL_VERIFICATION_RELOAD_MARKER_SKEW_MS && age <= EMAIL_VERIFICATION_RELOAD_MARKER_TTL_MS;
}

/**
* Handles user email verification UI and logic: validates input email, communicates with authentication services to verify accounts, sets verification state, and navigates to relevant routes.
* @component
* @selector app-email-verification
* @example
*   <app-email-verification></app-email-verification>
* @input {none} none - No external input properties; the component manages its own internal email and state.
* @output {none} none - Does not emit Angular output events; communicates via services and Router navigation.
*
* Key responsibilities:
* - Presents and validates an email address for verification and password reset flows.
* - Stores temporary verification state in sessionStorage/localStorage and interacts with UserService and LoginService.
* - Decodes and sets the identifier (when present) on the UserService upon successful verification and navigates to the login route; the verifier is fetched by UserService after token generation.
*
* Important properties:
* - showEmailValidator: boolean - toggles display of verification UI after successful validation.
* - resetPassword: boolean - indicates reset password flow (internal flag).
* - errorMessage: string - holds validation or server error messages for display.
* - email: string - bound to the email input field (internal state).
*
* Key methods:
* - verifyEmail(): void - Validates the email format, sets error messages for invalid input, sends validation request via LoginService, handles success by storing verification state, updating UserService, and navigating to '/login'; handles errors by setting errorMessage and clearing verification flag.
* - unlockAccount(): void - Navigates to the account unlock route ('/unlockAccount').
*
* Lifecycle hooks:
* - ngOnInit(): void - On init, redirects to legacy login in production, ensures a single reload cycle to clear session/local storage and prevent stale state, and clears storage after the reload guard.
*
* Notes:
* - No template bindings are exposed as @Input or @Output — communication is performed via injected services (UserService, LoginService) and Router navigation.
*/
@Component({
  selector: 'app-email-verification',
  standalone: true,
  imports: [CommonModule, FormsModule, TextBoxModule, ButtonModule, TranslateModule, AuthBrandPanelComponent, MigrationNoticeComponent],
  templateUrl: './email-verification.component.html',
  styleUrls: ['./email-verification.component.css', '../shared/styles/auth-shell.css'],
})
export class EmailVerificationComponent {
  showEmailValidator: boolean = false;
  resetPassword: boolean = false;
  errorMessage: string = '';
  email: string = '';

  constructor(private userService: UserService, private loginService: LoginService, private router: Router, private translate: TranslateService) { }
  /**
  * Initialize component on load: if in production redirect to the legacy login page; otherwise ensure a single reload on first visit then clear session and local storage.
  * The "already reloaded" guard lives in `window.name` (see {@link armReloadMarker} / {@link consumeReloadMarker}), NOT in sessionStorage, so
  * right after a logout the page holds no session-storage keys while the reload is pending. The marker is consumed (and the tab's previous
  * `window.name` restored) on the second load, the same lifecycle point where the old sessionStorage key was removed.
  * @example
  * gOnInit()
  * // void
  * @returns {void} No return value.
  */
  ngOnInit() {
    if (environment.production === true) {
      this.redirectToLogin(environment.LOGIN_URL);
      return;
    }

    if (!consumeReloadMarker(window)) {
      armReloadMarker(window);
      this.reloadPage();
      return;
    }

    sessionStorage.clear();
    localStorage.clear();
  }

  /**
  * Reloads the current page. Isolated so tests can observe the one-time reload without a real navigation.
  * @returns {void} No return value; the browser reloads the document.
  */
  protected reloadPage(): void {
    window.location.reload();
  }

  /**
  * Sends the browser to the legacy login page (production only). Isolated so tests can observe the redirect.
  * @param url - The legacy login URL (`environment.LOGIN_URL`).
  * @returns {void} No return value; the browser navigates away.
  */
  protected redirectToLogin(url: string): void {
    window.location.href = url;
  }

  /**
  * Validate the component's email property, submit it to the backend for verification, update session state and navigate on success or set error messages on failure.
  * @example
  * erifyEmail()
  * undefined
  * @param {{void}} none - No parameters; this method reads this.email and operates on component services/state.
  * @returns {{void}} No return value; side effects include HTTP call, sessionStorage updates, and navigation.
  **/
  verifyEmail() {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    if (!this.email || this.email.trim() === '') {
      this.errorMessage = this.translate.instant('app.email_verification.email_cannot_be_empty');
      return;
    }

    if (!emailRegex.test(this.email)) {
      this.errorMessage = this.translate.instant('app.email_verification.invalid_email_format');
      return;
    }
    sessionStorage.setItem('LoginEmail', this.email);
    const body = { email: this.email };

    this.loginService.validateEmail(body).subscribe({
      next: (response) => {
        if (response.status === 200 && response.body?.result) {

          this.showEmailValidator = true;
          // The verifier is no longer read from this response: UserService
          // fetches it (fetchVerifier) once the login's token mint succeeds.
          // identifier is no longer sent by /common/verify-email; guard so its
          // absence doesn't throw in atob() and block the navigation below.
          if (response.body.result.identifier) {
            this.userService.setIdentifier(atob(response.body.result.identifier));
          }
          sessionStorage.setItem('isVerified', 'true');
          this.router.navigate(['/login']);
        }
      },
      error: (error) => {
        this.errorMessage = error.error?.message || 'Invalid email. Please try again.';
        sessionStorage.removeItem('isVerified');
      }
    });
  }

  unlockAccount() {
    this.router.navigate(['/unlockAccount']);
  }

}